'use client';
import { Box, List, ListItem, Typography } from '@mui/material';
import { usePageAction } from '@repo/hooks/usePageAction';
import { LinkViewed } from '@repo/segment/Analytics/LinkViewed';
import type { MonetizableEnum } from '@repo/supergraph-schemas/marketplace/enums';
import type { RecapItem, StateAbbreviation } from '@repo/supergraph-schemas/marketplace/types';
import { filterEntitiesByState } from '../../utils/geo-data';
import { SafeLinkWithFallback } from '../SafeLinkWithFallback';

export type RecapListProps = {
  title: string;
  marketplaceProductType?: string;
  recapItems: {
    recapDescription?: string;
    marketplaceEntity: {
      id: string;
      name: string;
      reviewLink?: string;
      displayName?: string;
      monetizable?: MonetizableEnum;
    };
  }[];
  state?: StateAbbreviation;
  headingComponent?: React.ElementType;
};

export default function RecapListSection({
  title,
  recapItems,
  state,
  marketplaceProductType = '',
  headingComponent = 'h3',
}: RecapListProps): React.JSX.Element {
  const geoTargetedRecap = state ? filterEntitiesByState<RecapItem>(recapItems, state) : recapItems;

  // TODO: Segment Standardization Cleanup
  const fireEventAction = usePageAction({
    actionName: 'nw_review_redirected',
    actionSection: marketplaceProductType.toLowerCase(),
    actionType: 'select',
  });

  return (
    <Box id="recap-section" data-testid="recap-list-section">
      <Typography variant="text">To recap our selections...</Typography>
      <Typography
        id="recap"
        variant="textLargeBold"
        component={headingComponent}
        sx={{
          fontWeight: 700,
        }}
      >
        {title}
      </Typography>
      <List sx={{ listStyleType: 'disc', paddingLeft: 3, paddingY: 0 }}>
        {geoTargetedRecap.map(({ recapDescription, marketplaceEntity }, index) => {
          const { id, reviewLink, displayName } = marketplaceEntity;
          const handleClick =
            (fireLinkClicked: () => void) => (event: React.MouseEvent<HTMLAnchorElement>) => {
              if (marketplaceProductType) {
                const targetLink = event.currentTarget.getAttribute('href');
                fireEventAction({ ...(targetLink && { action_destination_url: targetLink }) });
              }
              fireLinkClicked();
            };
          return (
            <ListItem
              key={`recap-list-item-${id}`}
              sx={{ display: 'list-item', paddingY: '4px', paddingX: 0 }}
            >
              {reviewLink ? (
                <LinkViewed
                  entityName="nw_review_redirected"
                  sectionName="banking"
                  destinationUrl={reviewLink}
                  linkCopy={displayName ?? ''}
                  linkPosition={index}
                >
                  {({ fireLinkClicked }) => (
                    <SafeLinkWithFallback href={reviewLink} onClick={handleClick(fireLinkClicked)}>
                      {displayName}
                    </SafeLinkWithFallback>
                  )}
                </LinkViewed>
              ) : (
                <SafeLinkWithFallback href={reviewLink}>{displayName}</SafeLinkWithFallback>
              )}
              {recapDescription && `: ${recapDescription}`}
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
